import { type ReactNode, useState } from 'react';

import {
  Button,
  ListRowTextGroup,
  Menu,
  MenuRoot,
  MenuRow,
  Text,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import { MenuTriggerPlusIcon } from '@spotify/confidence-core-react';
import { type WorkflowV1WorkflowInstanceInput } from '@spotify/confidence-graphql';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import { type WorkflowExtensionOptions } from '../../extensions';
import { getWorkflowName } from '../../helpers';

const CreateExperimentMenuItem = ({
  ext,
  onSelect,
}: {
  ext: WorkflowExtensionOptions;
  onSelect: (ext: WorkflowExtensionOptions) => void;
}) => {
  const { allowed } = useCheckResourcePermissions({
    can: 'create_workflow_instance',
    name: getWorkflowName(ext.workflowId),
    optimistic: true,
  });

  const WorkflowIcon = ext.workflowIcon;
  const trackId = `menuitem.${ext.title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <MenuRow
      size={ext.description ? 'large' : 'small'}
      textValue={ext.title}
      isDisabled={!allowed}
      data-track-id={trackId}
      data-track-action="update"
      onAction={() => {
        onSelect(ext);
      }}
     data-track-source-anchor="ed2f0ce077;create-experiment-menu-item-menu-row;low">
      <WorkflowIcon />
      <ListRowTextGroup>
        <Text variant="bodySmallBold">{ext.title}</Text>
        {ext.description && (
          <Text variant="bodySmall" semanticColor="textSubdued">
            {ext.description}
          </Text>
        )}
      </ListRowTextGroup>
    </MenuRow>
  );
};

export type CreateExperimentMenuProps = {
  extensions: WorkflowExtensionOptions[];
  overrides?: Partial<WorkflowV1WorkflowInstanceInput>;
  dialogSubtitle?: string;
};

export const CreateExperimentMenu = ({
  extensions,
  overrides,
  dialogSubtitle,
}: CreateExperimentMenuProps): ReactNode => {
  const [createDialogExtension, setCreateDialogExtension] = useState<
    WorkflowExtensionOptions | undefined
  >();
  const CreateDialog = createDialogExtension?.createDialog;

  return (
    <>
      <MenuRoot>
        <Button variant="primary" size="small" id="create-experiment-button" data-track-source-anchor="051c9764e5;;high">
          <MenuTriggerPlusIcon />
          Create
        </Button>
        <Popover placement="bottomEnd">
          <Menu aria-label="Create experiment">
            {extensions.map(ext => (
              <CreateExperimentMenuItem
                key={ext.workflowId}
                ext={ext}
                onSelect={setCreateDialogExtension}
               data-track-source-anchor="c0943daf76;create-experiment-menu-create-experiment-menu"/>
            ))}
          </Menu>
        </Popover>
      </MenuRoot>
      {CreateDialog && (
        <CreateDialog
          open
          onClose={() => setCreateDialogExtension(undefined)}
          overrides={overrides}
          subtitle={dialogSubtitle}
         data-track-source-anchor="d8ec2a782f;create-experiment-menu-create-dialog"/>
      )}
    </>
  );
};
